import { Capacitor, registerPlugin } from '@capacitor/core';

const IcsExport = registerPlugin('IcsExport');

export function isNativePlatform() {
  return Boolean(Capacitor.isNativePlatform?.());
}

export function isNativeAndroid() {
  return Capacitor.isNativePlatform?.() && Capacitor.getPlatform?.() === 'android';
}

export function isNativeIOS() {
  return Capacitor.isNativePlatform?.() && Capacitor.getPlatform?.() === 'ios';
}

function blobToBase64(blob) {
  return blob.arrayBuffer().then((buffer) => {
    const bytes = new Uint8Array(buffer);
    const chunkSize = 0x8000;
    let binary = '';

    for (let i = 0; i < bytes.length; i += chunkSize) {
      const chunk = bytes.subarray(i, i + chunkSize);
      binary += String.fromCharCode(...chunk);
    }

    return btoa(binary);
  });
}

async function openTextFileViaPlugin(content, filename, mimeType) {
  if (!isNativePlatform() || !IcsExport) return false;

  if (mimeType === 'text/calendar' && IcsExport?.exportIcs) {
    await IcsExport.exportIcs({
      content,
      filename,
    });
  } else {
    await IcsExport.openFile({
      content,
      filename,
      mimeType,
    });
  }
  return true;
}

async function openBlobFileViaPlugin(blob, filename, mimeType) {
  if (!isNativePlatform() || !IcsExport?.openFile) return false;
  const base64 = await blobToBase64(blob);
  await IcsExport.openFile({
    base64,
    filename,
    mimeType,
  });
  return true;
}

export async function openTextFileOnNative(content, filename, mimeType) {
  return openTextFileViaPlugin(content, filename, mimeType);
}

export async function openTextFileOnAndroid(content, filename, mimeType) {
  return openTextFileViaPlugin(content, filename, mimeType);
}

export async function openBlobFileOnAndroid(blob, filename, mimeType) {
  return openBlobFileViaPlugin(blob, filename, mimeType);
}

export async function openBlobFileOnNative(blob, filename, mimeType, options = {}) {
  if (isNativePlatform() && options.preferShare) {
    const file = new File([blob], filename, { type: mimeType });

    if (navigator.share && navigator.canShare?.({ files: [file] })) {
      await navigator.share({
        title: options.title || filename,
        text: options.text,
        files: [file],
      });
      return true;
    }
  }

  if (await openBlobFileViaPlugin(blob, filename, mimeType)) {
    return true;
  }

  if (!isNativePlatform()) {
    return false;
  }

  const file = new File([blob], filename, { type: mimeType });

  if (navigator.share && navigator.canShare?.({ files: [file] })) {
    await navigator.share({
      title: options.title || filename,
      text: options.text,
      files: [file],
    });
    return true;
  }

  const url = URL.createObjectURL(blob);
  window.open(url, '_blank', 'noopener,noreferrer');
  setTimeout(() => URL.revokeObjectURL(url), 60000);
  return true;
}

export async function importEventsToNativeCalendar(events) {
  if (!isNativeIOS() || !IcsExport?.importEvents) {
    return false;
  }

  await IcsExport.importEvents({ events });
  return true;
}

export function openExternalUrl(url, options = {}) {
  if (!url || typeof window === 'undefined') {
    return false;
  }

  const target = options.sameWindow ? '_self' : '_blank';
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.target = target;
  anchor.rel = 'noopener noreferrer';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();

  if (isNativePlatform() && options.fallbackToLocation !== false) {
    window.setTimeout(() => {
      if (document.visibilityState === 'visible') {
        window.location.href = url;
      }
    }, 400);
  }

  return true;
}
